import React, { useState, useEffect, useMemo } from "react";
import {
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  TableCellLayout,
  Button,
  Input,
  Spinner,
  useTableFeatures,
  useTableSort,
  createTableColumn,
} from "@fluentui/react-components";
import { CiFilter } from "react-icons/ci";
import { DeleteRegular, Add20Filled, EditRegular, EyeRegular } from "@fluentui/react-icons";
import { useMsal } from "@azure/msal-react";
import { useTranslation } from "react-i18next";
import "../CSS/ListAgentsComponent.css";
import { getAccessToken } from "../../Services/GraphServices";
import { DeleteAgent ,GetAllAgent} from "../../Services/HeedifyServices";
import { ConfirmAlert } from "../GenericComponent/ConfirmAlert";
import { useNavigate } from "react-router-dom";
import { useAbility } from "../../Contexts/AbilityContext";
import InfoButton from "../GenericComponent/InfoButton";
import { useNotification } from "../GenericComponent/NotificationSystem";

const ListAgentsComponent = (props) => {
  const { instance, accounts } = useMsal();
  const activeAccount = accounts[0];
  const userId = activeAccount?.localAccountId;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const ability = useAbility();
  const { showSuccess, showError } = useNotification();

  const [agentsRows, setAgentsRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [deletingAgentId, setDeletingAgentId] = useState(null);
  const [filterVisible, setFilterVisible] = useState({
    NAME: false,
    EMAIL: false,
    ENTITY: false,
    LICENCE_LEVEL: false,
  });
  // Filtres individuels
  const [filters, setFilters] = useState({
    NAME: "",
    EMAIL: "",
    ENTITY: "",
    LICENCE_LEVEL: "",
  });
  
  useEffect(() => {
    if (props.AGENTS && props.AGENTS.length > 0) {
      // 🔐 Calculer permissions pour chaque agent
      const agentsWithPermissions = props.AGENTS.map(agent => ({
        ...agent,
        permissions: {
          main: {
            canCreate: ability?.can("create", `Agent.main@${agent.scope}`),
            canRead: ability?.can("read", `Agent.main@${agent.scope}`),
            canUpdate: ability?.can("update", `Agent.main@${agent.scope}`),
            canDelete: ability?.can("delete", `Agent.main@${agent.scope}`),
          },
        },
      }));
      setAgentsRows(agentsWithPermissions);
      setFilteredRows(agentsWithPermissions);
    }
  }, [props.AGENTS, ability]);
  // --- Fonction de filtrage externe ---
  const applyFilters = (agentsRows, filters) => {
    return agentsRows.filter((agent) => {
      const matchName = agent.displayName
        ?.toLowerCase()
        .includes(filters.NAME.toLowerCase());
      const matchEmail = agent.email
        ?.toLowerCase()
        .includes(filters.EMAIL.toLowerCase());
      const matchEntity = agent.scope
        ?.toLowerCase()
        .includes(filters.ENTITY.toLowerCase());
      const matchLicence = agent.licenceLevel
        ?.toLowerCase()
        .includes(filters.LICENCE_LEVEL.toLowerCase());
      // 🔐 Filtrer par permission canRead
      const hasReadPermission = agent.permissions?.main?.canRead;

      return matchName && matchEmail && matchEntity && matchLicence && hasReadPermission;
    });
  };

  // --- useEffect qui applique le filtre ---
  useEffect(() => {
    const newFiltered = applyFilters(agentsRows, filters);
    setFilteredRows(newFiltered);
  }, [filters, agentsRows]);

  // --- Tri avec Sorting ---
  const columns = [
    createTableColumn({
      columnId: "NAME",
      compare: (a, b) => a.displayName?.localeCompare(b.displayName || "") ?? 0,
    }),
    createTableColumn({
      columnId: "EMAIL",
      compare: (a, b) => a.email?.localeCompare(b.email || "") ?? 0,
    }),
    createTableColumn({
      columnId: "ENTITY",
      compare: (a, b) => a.scope?.localeCompare(b.scope || "") ?? 0,
    }),
    createTableColumn({
      columnId: "LICENCE_LEVEL",
      compare: (a, b) =>
        a.licenceLevel?.localeCompare(b.licenceLevel || "") ?? 0,
    }),
    createTableColumn({ columnId: "ACTION" }),
  ];

  const {
    getRows,
    sort: { getSortDirection, toggleColumnSort, sort },
  } = useTableFeatures({ columns, items: filteredRows }, [
    useTableSort({
      defaultSortState: { sortColumn: "NAME", sortDirection: "ascending" },
    }),
  ]);

  /* const rows =   sort(getRows());*/
  const rows = useMemo(() => {
    return sort(getRows());
  }, [sort, getRows]);
  const headerSortProps = (columnId) => ({
    onClick: (e) => toggleColumnSort(e, columnId),
    sortDirection: getSortDirection(columnId),
  });
  const handleFilterChange = (columnKey, value) => {
    setFilters((prev) => ({ ...prev, [columnKey]: value }));
  };

  // Toggle the visibility of a filter input field
  const toggleFilterVisibility = (column) => {
    setFilterVisible((prev) => ({ ...prev, [column]: !prev[column] }));
  };
  // --- Suppression ---
  const removeAgent = (key, agentId, scope) => {
      setDeletingAgentId(agentId);
      getAccessToken(instance, activeAccount)
              .then((token) => {
    return DeleteAgent(props.ACTIVE_TENANT, userId, agentId, scope ,token);
      })
      .then((response) => {
        if (response && response.message === "deleted" && response.success === true) {
          // Update the global agents state
          props.setAgents(props.AGENTS.filter(agent => agent.id !== agentId));
          setAgentsRows((prev) => prev.filter((_, i) => i !== key));
          showSuccess(t("AGENT_DELETED_SUCCESS"));
        } else if (response) {
          console.error("❌ Failed to delete agent:", response.message);
          showError(t("AGENT_DELETE_FAILED"));
        }
      })
      .catch((error) => {
        console.error("❌ Error deleting agent:", error);
        showError(t("AGENT_DELETE_ERROR"));
      })
      .finally(() => {
        setDeletingAgentId(null);
        setSelectedAgent(null);
      });
  };

  const confirmRemoveAgent = (agent, index) => {
    setSelectedAgent({ ...agent, key: index });
  };

  const addAgent = () => {
    navigate(`/agents/newAgent`);
  };
  const selectAgent = (event, data) => {
    navigate(`/agents/${data}`);
  };

  // 🔐 Vérifier si on peut créer un agent dans au moins un scope
  const hasAnyCreatePermission = props.SCOPES_NAMES?.some(scope => 
    ability?.can("create", `Agent.main@${scope}`)
  );

  return (
    <>
      <div >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <h2>{t("AGENT_LIST")}</h2>
        <Button
          appearance="primary"
          onClick={addAgent}
          className="agent-list-button-style"
          disabled={!hasAnyCreatePermission}
        >
          <Add20Filled /> {t("ADD_NEW_AGENT")}
        </Button>
      </div>

      <div className="table-container">
<div  className="agent-list-table-wrapper">
        <Table
          role="grid"
          aria-label="Agents table with filters and sorting"
          className="agent-list-table-style"
          sortable
        >
          <TableHeader>
            {/* Ligne des titres triables */}
            <TableRow className="agent-list-header-style">
              <TableHeaderCell className="agent-list-cell-bold" {...headerSortProps("NAME")}>
              <div className="agent-list-header-filter-container">
                <div className="agent-list-div-filter-style">
                  {t("NAME")}
                  <CiFilter
                    className="agent-list-filter-icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFilterVisibility("NAME");
                    }}
                  />
                  {filterVisible.NAME && (
                    <Input
                      className="agent-list-input-filter-inline"
                      value={filters.NAME}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e, data) =>
                        handleFilterChange("NAME", data.value)
                      }
                      placeholder={t("FILTER")}
                      size="small"
                    />
                  )}
                </div>
              </div>
              </TableHeaderCell>
              <TableHeaderCell className="agent-list-cell-bold" {...headerSortProps("EMAIL")}>
              <div className="agent-list-header-filter-container">
                <div className="agent-list-div-filter-style">
                  {t("EMAIL")}
                  <CiFilter
                    className="agent-list-filter-icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFilterVisibility("EMAIL");
                    }}
                  />
                  {filterVisible.EMAIL && (
                    <Input
                      className="agent-list-input-filter-inline"
                      value={filters.EMAIL}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e, data) =>
                        handleFilterChange("EMAIL", data.value)
                      }
                      placeholder={t("FILTER")}
                      size="small"
                    />
                  )}
                </div>
              </div>
              </TableHeaderCell>
              <TableHeaderCell className="agent-list-cell-bold" {...headerSortProps("ENTITY")}>
              <div className="agent-list-header-filter-container">
                <div className="agent-list-div-filter-style">
                  {t("SCOPE")}
                  <CiFilter
                    className="agent-list-filter-icon"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleFilterVisibility("ENTITY");
                    }}
                  />
                  {filterVisible.ENTITY && (
                    <Input
                      className="agent-list-input-filter-inline"
                      value={filters.ENTITY}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e, data) =>
                        handleFilterChange("ENTITY", data.value)
                      }
                      placeholder={t("FILTER")}
                      size="small"
                    />
                  )}
                </div>
              </div>
              </TableHeaderCell>
              <TableHeaderCell className="agent-list-cell-bold" {...headerSortProps("LICENCE_LEVEL")}>
              <div className="agent-list-header-filter-container">
                <div className="agent-list-div-filter-style">
                  {t("LICENCE")}
                  <InfoButton infoKey="LICENCE_LEVEL" category="AGENTS" />
                  <CiFilter
                    className="agent-list-filter-icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFilterVisibility("LICENCE_LEVEL");
                    }}
                  />
                  {filterVisible.LICENCE_LEVEL && (
                    <Input
                      className="agent-list-input-filter-inline"
                      value={filters.LICENCE_LEVEL}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e, data) =>
                        handleFilterChange("LICENCE_LEVEL", data.value)
                      }
                      placeholder={t("FILTER")}
                      size="small"
                    />
                  )}
                </div>
              </div>
              </TableHeaderCell>
              <TableHeaderCell className="agent-list-cell-bold agent-list-cell-right">{t("ACTION")}</TableHeaderCell>
            </TableRow>
          </TableHeader>

          <TableBody >
            {rows.map(({ item, rowId }) => {
              return (
                <TableRow key={rowId}>
                  <TableCell>
                    <TableCellLayout>{item.displayName}</TableCellLayout>
                  </TableCell>
                  <TableCell>
                    <TableCellLayout>{item.email}</TableCellLayout>
                  </TableCell>
                  <TableCell>
                    <TableCellLayout>{item.scope}</TableCellLayout>
                  </TableCell>
                  <TableCell>
                    <TableCellLayout>{t(item.licenceLevel)}</TableCellLayout>
                  </TableCell>
                  <TableCell className="agent-list-cell-right">
                    {/* Ordre constant dans tout le portail : CONSULTER à gauche, SUPPRIMER à
                        droite. Le geste destructeur est le plus éloigné du geste courant, et il se
                        trouve toujours au même endroit d'un écran à l'autre. */}
                    <Button
                      appearance="subtle"
                      icon={item.permissions?.main?.canUpdate ? <EditRegular /> : <EyeRegular />}
                      aria-label={item.permissions?.main?.canUpdate ? "Edit" : "View"}
                      onClick={(event) => selectAgent(event, item.id)}
                      disabled={!item.permissions?.main?.canRead}
                    />
                    <Button
                      appearance="subtle"
                      icon={deletingAgentId === item.id ? <Spinner size="tiny" /> : <DeleteRegular />}
                      aria-label="Delete"
                      onClick={() => confirmRemoveAgent(item, rowId)}
                      disabled={!item.permissions?.main?.canDelete || deletingAgentId === item.id}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        </div>
      </div>

      {selectedAgent && (
        <ConfirmAlert
          open={!!selectedAgent}
          name={selectedAgent.displayName}
          onConfirm={() =>
            removeAgent(
              selectedAgent.key,
              selectedAgent.id,
              selectedAgent.scope
            )
          }
          onCancel={() => setSelectedAgent(null)}
        />
      )}
      </div>
    </>
  );
};

export default ListAgentsComponent;
